import { useEffect, useCallback, useRef } from 'react';
import type { IFormStep } from '~/types';

// Trusted origins for postMessage events
const TRUSTED_ORIGINS = ['https://calendly.com', 'https://assets.calendly.com', 'https://calendly-embed.com'];

// Helper to check if event data looks like a Calendly event
const isCalendlyEvent = (data: unknown): boolean => {
  if (typeof data !== 'object' || data === null) return false;
  const dataObj = data as Record<string, unknown>;
  return typeof dataObj.event === 'string' && dataObj.event.startsWith('calendly.');
};

interface UseIframeEventsOptions {
  currentStepConfig: IFormStep | undefined;
  onAdvance: () => void;
  onEventReceived?: (eventType: string, data: unknown) => void;
  enabled?: boolean;
}

interface UseIframeEventsResult {
  lastEvent: { type: string; data: unknown } | null;
}

export const useIframeEvents = ({
  currentStepConfig,
  onAdvance,
  onEventReceived,
  enabled = true,
}: UseIframeEventsOptions): UseIframeEventsResult => {
  const lastEventRef = useRef<{ type: string; data: unknown } | null>(null);

  // Check if current step should auto-advance on postMessage
  const shouldListenForPostMessage = useCallback((): boolean => {
    if (!currentStepConfig?.autoAdvance?.enabled) return false;
    return currentStepConfig.autoAdvance.trigger === 'postMessage';
  }, [currentStepConfig]);

  // Get expected event type from step config
  const getExpectedEventType = useCallback((): string | undefined => {
    return currentStepConfig?.autoAdvance?.eventType;
  }, [currentStepConfig]);

  // Validate message origin
  const isValidOrigin = useCallback((origin: string): boolean => {
    return TRUSTED_ORIGINS.some((trusted) => origin.startsWith(trusted));
  }, []);

  // Extract event type from message data
  const extractEventType = useCallback((data: unknown): string | null => {
    if (typeof data !== 'object' || data === null) return null;

    const dataObj = data as Record<string, unknown>;

    // Calendly uses 'event' property
    if (typeof dataObj.event === 'string') {
      return dataObj.event;
    }

    // Generic 'type' property
    if (typeof dataObj.type === 'string') {
      return dataObj.type;
    }

    return null;
  }, []);

  useEffect(() => {
    if (!enabled || !shouldListenForPostMessage()) return;

    const expectedEventType = getExpectedEventType();
    if (!expectedEventType) return;

    const handleMessage = (event: MessageEvent) => {
      // Validate origin - either trusted origin or Calendly-specific event structure
      const originValid = isValidOrigin(event.origin);
      const isCalendly = isCalendlyEvent(event.data);

      if (!originValid && !isCalendly) {
        return;
      }

      const eventType = extractEventType(event.data);
      if (!eventType) return;

      // Store the event
      lastEventRef.current = { type: eventType, data: event.data };

      // Notify about the event
      onEventReceived?.(eventType, event.data);

      // Check if this matches the expected event type for auto-advance
      if (eventType === expectedEventType) {
        onAdvance();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    enabled,
    shouldListenForPostMessage,
    getExpectedEventType,
    isValidOrigin,
    extractEventType,
    onAdvance,
    onEventReceived,
  ]);

  return {
    lastEvent: lastEventRef.current,
  };
};
